import type { Money } from "../api/types";

/**
 * The zone an account's timestamps are shown in.
 *
 * Not the browser's zone and not the server's. An account is a salon in Chennai
 * and its owner may open the app from Dubai; every timestamp on that screen
 * belongs in the *account's* zone, or two panels showing the same booking
 * disagree by a few hours and the customer turns up at the wrong one.
 *
 * `TIME_ZONE = "UTC"` server-side, so anything rendered on the server is UTC.
 * That makes the client the only place the account's zone can be applied, which
 * is why this lives here rather than in a template.
 */
export function zoneFormatter(
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat | null {
  if (!timeZone) return null;
  try {
    // Throws on an unknown IANA name rather than silently falling back to UTC,
    // which would be a wrong time on someone's screen.
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone });
  } catch {
    return null;
  }
}

/** Currencies the account can be denominated in. */
const CURRENCY_LOCALE: Record<string, string> = {
  INR: "en-IN",
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB",
  AED: "ar-AE",
};

const FALLBACK_DATE = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
});
const FALLBACK_DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** A calendar date has no time and no zone of its own. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Shown in UTC so the calendar day survives whatever zone the reader is in. */
const CALENDAR_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * Money arrives as `{amount: "1250.00", currency: "INR"}`. It is formatted here
 * and nowhere else, so a contact list, an invoice and a dashboard can never
 * disagree about how a figure reads.
 */
export function formatMoney(money: Money | null | undefined): string {
  const amount = Number(money?.amount ?? 0);
  const currency = money?.currency ?? "INR";
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE[currency] ?? undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unknown code should not blank the cell it appears in.
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat().format(n);
}

/**
 * Sum a list of `{amount, currency}` into one.
 *
 * Returns null for an empty list or when every amount is zero, so a caller can
 * distinguish "worth nothing" from "nothing there" — a contact with no open
 * enquiries should not display a confident ₹0.00 as though it were a figure.
 */
export function sumMoney(items: Array<Money | null | undefined>): Money | null {
  const usable = items.filter((m): m is Money => Boolean(m?.amount));
  if (usable.length === 0) return null;

  const currency = usable[0]!.currency ?? "INR";
  // Anything in a different currency is excluded rather than added: summing
  // rupees into dollars is worse than showing nothing.
  const sameCurrency = usable.filter((m) => (m.currency ?? "INR") === currency);
  const total = sameCurrency.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);

  return total === 0 ? null : { amount: total.toFixed(2), currency };
}

/**
 * A day count is a decimal on the wire, but the payroll serializer emits it as
 * a `{amount, currency}` object. React throws on an object child and, with no
 * error boundary, that blanks the whole page -- so coerce either shape (a bare
 * string, or that money-like object) to a string before it is rendered.
 */
export function formatDayCount(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    const amount = (value as { amount?: unknown }).amount;
    return amount === null || amount === undefined ? "—" : String(amount);
  }
  return String(value);
}

export function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** ISO-8601 on the wire, always UTC. Rendered in the account's zone. */
export function formatDate(
  iso: string | null | undefined,
  timeZone?: string,
): string {
  if (!iso) return "—";
  // A calendar date ("2026-10-22") is a day, not an instant. Reading it as UTC
  // midnight and then formatting it in the account's zone shows the day before
  // for any account behind UTC -- a leave dated the 22nd would read as the 21st.
  if (CALENDAR_DATE.test(iso)) {
    const calendar = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(calendar.getTime())) return "—";
    return CALENDAR_DATE_FORMAT.format(calendar);
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return (zoneFormatter(timeZone, { day: "2-digit", month: "short", year: "numeric" }) ?? FALLBACK_DATE).format(parsed);
}

/**
 * The wall-clock time in the account's zone -- "09:42".
 *
 * Separate from `formatDateTime` because a live clock does not need the date
 * repeated every second, and because it takes a `Date` as well as an ISO
 * string: the card ticks a local clock, it does not re-read the server's.
 */
export function formatClockTime(
  value: Date | number | string | null | undefined,
  timeZone?: string,
): string {
  if (value == null) return "--:--";
  const parsed =
    value instanceof Date ? value : new Date(typeof value === "number" ? value : value);
  if (Number.isNaN(parsed.getTime())) return "--:--";
  const formatter = zoneFormatter(timeZone, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    formatter ??
    new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" })
  ).format(parsed);
}

export function formatDateTime(
  iso: string | null | undefined,
  timeZone?: string,
): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  const formatter = zoneFormatter(timeZone, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (formatter ?? FALLBACK_DATE_TIME).format(parsed);
}

/**
 * Relative time for "last contacted 3 days ago" copy.
 *
 * Zone-independent on purpose: "3 days ago" is the same fact in every zone, and
 * recomputing it per-zone would make the number flicker as you cross one.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "Never";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";

  const diffMs = Date.now() - parsed.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;

  return FALLBACK_DATE.format(parsed);
}

/**
 * Elapsed time since a moment, at a resolution a person can act on.
 *
 * Distinct from `formatRelative` on purpose. "3d ago" is for a list of things
 * that happened; "2d 6h since last contact" is for judging whether anybody has
 * actually followed up, and a single coarse bucket hides the difference between
 * a lead chased this morning and one ignored for two and a half days.
 *
 * Only two units, never three: "1mo" is not actionable on a sales desk.
 */
export function formatElapsed(
  iso: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

/**
 * The API's `primary_object_label` is singular by design -- "Client",
 * "Patient", "Buyer" -- because it is what one record is called. The UI needs
 * the plural for list chrome ("All clients", "No clients yet").
 *
 * A naive `+ "s"` rather than a real pluraliser: the vocabulary is a closed set
 * of industry labels, and none of them end in a letter that needs anything
 * other than an "s". If that ever stops being true, this is the place to change.
 */
export function plural(label: string): string {
  const lower = label.toLowerCase();
  if (/(s|x|z|ch|sh)$/.test(lower)) return lower;
  if (/[^aeiou]y$/.test(lower)) return `${lower.slice(0, -1)}ies`;
  return `${lower}s`;
}

/** "1 module" / "2 modules" without a second conditional at the call site. */
export function countLabel(n: number, singular: string, plural_?: string): string {
  return `${n} ${n === 1 ? singular : (plural_ ?? plural(singular))}`;
}

/**
 * "full_time" -> "Full Time", "notice_period" -> "Notice Period". Display only:
 * the wire value stays canonical so filtering and the API never depend on the
 * label. Shared so every screen humanises enum values the same way.
 */
export function humanizeEnum(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}